import React from "react";
import PropTypes from "prop-types";
import { Card } from "react-bootstrap";

// editable adds a subordinate reorder/feature/edit/delete row to the footer -- outline-secondary sm,
// matching the chassis every other profile-section owner control uses (never
// outline-primary/outline-danger). Up/down move this row to its neighbor's list_position (a
// server-side swap, see Quotes' moveQuote); canMoveUp/canMoveDown come from the row's position within
// the loaded (list_position-ordered) array, so the first row can't move up and the last can't move
// down. The featured star is a plain fas/far swap (no color-only signal -- filled vs. outline is the
// state, matching the edit/delete icon-only buttons' existing convention here). `reorderDisabled` is
// separate from `disabled`: it only covers the up/down arrows and the featured star (not edit/delete),
// so a caller can block reordering without touching the rest of the row's controls.
function Quote({
  quote,
  editable = false,
  onEdit = () => {},
  onDelete = () => {},
  onMoveUp = () => {},
  onMoveDown = () => {},
  onToggleFeatured = () => {},
  canMoveUp = false,
  canMoveDown = false,
  disabled = false,
  reorderDisabled = false,
}) {
  return (
    <Card className={quote.featured ? "qw-quote-card qw-featured-quote" : "qw-quote-card border-accent-blue-10"}>
      <Card.Body>
        <i className="qw-quote-mark fas fa-quote-right float-end text-neutral-60 ms-3 mb-2" aria-hidden="true" />
        {quote.featured && (
          <span className="qw-featured-badge badge rounded-pill d-inline-flex align-items-center fw-bold paragraph-sm mb-2">
            <i className="fas fa-star me-1" />
            Featured
          </span>
        )}
        <Card.Text className={quote.featured ? "fw-bold" : ""}>{quote.quote_text}</Card.Text>
      </Card.Body>
      {editable && (
        <Card.Footer className="d-flex justify-content-end align-items-center">
          <div className="d-flex flex-wrap justify-content-end gap-1">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary d-flex"
              aria-label="Move quote up"
              onClick={onMoveUp}
              disabled={disabled || reorderDisabled || !canMoveUp}
            >
              <i className="fas fa-arrow-up" />
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary d-flex"
              aria-label="Move quote down"
              onClick={onMoveDown}
              disabled={disabled || reorderDisabled || !canMoveDown}
            >
              <i className="fas fa-arrow-down" />
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary d-flex"
              aria-label={quote.featured ? "Unfeature quote" : "Feature quote"}
              onClick={onToggleFeatured}
              disabled={disabled || reorderDisabled}
            >
              <i className={quote.featured ? "fas fa-star" : "far fa-star"} />
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary d-flex"
              aria-label="Edit quote"
              onClick={onEdit}
              disabled={disabled}
            >
              <i className="fas fa-edit" />
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary d-flex"
              aria-label="Remove quote"
              onClick={onDelete}
              disabled={disabled}
            >
              <i className="fas fa-trash text-destructive-90" />
            </button>
          </div>
        </Card.Footer>
      )}
    </Card>
  );
}

Quote.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  quote: PropTypes.object.isRequired,
  editable: PropTypes.bool,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onMoveUp: PropTypes.func,
  onMoveDown: PropTypes.func,
  onToggleFeatured: PropTypes.func,
  canMoveUp: PropTypes.bool,
  canMoveDown: PropTypes.bool,
  disabled: PropTypes.bool,
  reorderDisabled: PropTypes.bool,
};

export default Quote;
